/**
 * Parqet API service: OAuth PKCE, API client, and conversion to TransaktionsDaten.
 * See docs/PARQET_API_INTEGRATION_REFERENCE.md.
 */

import { TransaktionsDaten } from '@/types';
import { getParqetConfig, PARQET_SCOPE } from '@/utils/parqetConfig';
import type {
  TokenResponse,
  ParqetPortfolioListItem,
  ParqetActivity,
  ParqetActivityAsset,
  ParqetHolding,
  ParqetPerformanceResponse,
  ParqetActivitiesResponse,
} from '@/types/parqet';

const AUTH_AUTHORIZE_URL = 'https://connect.parqet.com/oauth2/authorize';

const STORAGE_KEYS = {
  CODE_VERIFIER: 'parqet_code_verifier',
  STATE: 'parqet_state',
  ACCESS_TOKEN: 'parqet_access_token',
  REFRESH_TOKEN: 'parqet_refresh_token',
  EXPIRES_AT: 'parqet_expires_at',
} as const;

/** Uses sessionStorage for PKCE code_verifier/state (persists across same-tab redirect, cleared when tab closes). */
const oauthStorage = {
  set(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // ignore
    }
  },
  get(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  remove(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
  },
};

/** Uses sessionStorage for tokens (cleared when tab closes). */
const tokenStorage = {
  set(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // ignore
    }
  },
  get(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  remove(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
  },
};

function base64URLEncode(buffer: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...buffer));
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64URLEncode(new Uint8Array(hash));
}

function generateRandomState(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
}

/**
 * Build OAuth authorization URL and store code_verifier and state in sessionStorage.
 */
export async function buildAuthorizationUrl(): Promise<string> {
  const config = getParqetConfig();
  if (!config.isConfigured) {
    throw new Error('Parqet ist nicht konfiguriert (VITE_PARQET_CLIENT_ID, VITE_PARQET_REDIRECT_URI).');
  }
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateRandomState();
  oauthStorage.set(STORAGE_KEYS.CODE_VERIFIER, codeVerifier);
  oauthStorage.set(STORAGE_KEYS.STATE, state);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: PARQET_SCOPE,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    state,
  });
  return `${AUTH_AUTHORIZE_URL}?${params.toString()}`;
}

/**
 * Exchange authorization code for tokens. Call from callback page.
 */
export async function exchangeCodeForToken(code: string): Promise<TokenResponse> {
  const config = getParqetConfig();
  const codeVerifier = oauthStorage.get(STORAGE_KEYS.CODE_VERIFIER);
  if (!codeVerifier) {
    throw new Error('Code-Verifier nicht gefunden. Bitte Anmeldung erneut starten.');
  }

  const params = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: config.redirectUri,
    client_id: config.clientId,
    code_verifier: codeVerifier,
  });

  const response = await fetch(config.tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: params.toString(),
  });
  if (!response.ok) {
    const errText = await response.text();
    throw new Error(`Token-Austausch fehlgeschlagen: ${errText}`);
  }

  oauthStorage.remove(STORAGE_KEYS.CODE_VERIFIER);
  const data = (await response.json()) as TokenResponse;
  return data;
}

/**
 * Refresh access token using refresh_token.
 */
export async function refreshAccessToken(refreshToken: string): Promise<TokenResponse> {
  const config = getParqetConfig();
  const params = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    client_id: config.clientId,
  });
  const response = await fetch(config.tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: params.toString(),
  });
  if (!response.ok) {
    throw new Error(`Token-Aktualisierung fehlgeschlagen: ${await response.text()}`);
  }
  return response.json() as Promise<TokenResponse>;
}

export function getStoredState(): string | null {
  return oauthStorage.get(STORAGE_KEYS.STATE);
}

export function clearStoredState(): void {
  oauthStorage.remove(STORAGE_KEYS.STATE);
}

export interface StoredTokens {
  accessToken: string;
  refreshToken: string | null;
  expiresAt: number;
}

export function getStoredTokens(): StoredTokens | null {
  const accessToken = tokenStorage.get(STORAGE_KEYS.ACCESS_TOKEN);
  const refreshToken = tokenStorage.get(STORAGE_KEYS.REFRESH_TOKEN);
  const expiresAt = tokenStorage.get(STORAGE_KEYS.EXPIRES_AT);
  if (!accessToken) return null;
  return {
    accessToken,
    refreshToken,
    expiresAt: expiresAt ? Number(expiresAt) : 0,
  };
}

export function setStoredTokens(data: TokenResponse): void {
  const expiresAt = Date.now() + (data.expires_in ?? 0) * 1000;
  tokenStorage.set(STORAGE_KEYS.ACCESS_TOKEN, data.access_token);
  if (data.refresh_token) {
    tokenStorage.set(STORAGE_KEYS.REFRESH_TOKEN, data.refresh_token);
  }
  tokenStorage.set(STORAGE_KEYS.EXPIRES_AT, String(expiresAt));
}

export function clearStoredTokens(): void {
  tokenStorage.remove(STORAGE_KEYS.ACCESS_TOKEN);
  tokenStorage.remove(STORAGE_KEYS.REFRESH_TOKEN);
  tokenStorage.remove(STORAGE_KEYS.EXPIRES_AT);
  oauthStorage.remove(STORAGE_KEYS.STATE);
}

async function apiRequest<T>(
  baseUrl: string,
  endpoint: string,
  accessToken: string,
  options?: { method?: string; body?: object }
): Promise<T> {
  const { method = 'GET', body } = options ?? {};
  const url = `${baseUrl}${endpoint}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  const res = await fetch(url, {
    method,
    headers,
    body: body && (method === 'POST' || method === 'PUT') ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    throw new Error(`API ${res.status}: ${await res.text()}`);
  }
  return res.json() as Promise<T>;
}

async function fetchPortfoliosList(baseUrl: string, accessToken: string): Promise<{ items: ParqetPortfolioListItem[] }> {
  return apiRequest(baseUrl, '/portfolios', accessToken);
}

async function fetchPerformanceForPortfolio(
  baseUrl: string,
  accessToken: string,
  portfolioId: string
): Promise<ParqetPerformanceResponse> {
  return apiRequest(baseUrl, '/performance', accessToken, {
    method: 'POST',
    body: { portfolioIds: [portfolioId] },
  });
}

/** Fetch all activities in one request (proxy handles pagination server-side). */
async function fetchAllActivitiesPages(
  baseUrl: string,
  accessToken: string,
  portfolioId: string
): Promise<ParqetActivity[]> {
  const res = await apiRequest<ParqetActivitiesResponse>(baseUrl, `/portfolios/${portfolioId}/activities-all`, accessToken);
  return res.activities ?? [];
}

function getAssetKey(asset: ParqetActivityAsset): string {
  switch (asset.assetIdentifierType) {
    case 'isin':
      return `isin:${asset.isin}`;
    case 'crypto_symbol':
      return `symbol:${asset.symbol}`;
    default:
      return `holding:${(asset as { holding_id: string }).holding_id}`;
  }
}

function getActivityIdentifier(asset: ParqetActivityAsset): string {
  switch (asset.assetIdentifierType) {
    case 'isin':
      return asset.isin;
    case 'crypto_symbol':
      return asset.symbol;
    default:
      return (asset as { holding_id: string }).holding_id;
  }
}

function holdingKey(h: ParqetHolding): string {
  if (h.asset?.isin) return `isin:${h.asset.isin}`;
  if (h.asset?.symbol) return `symbol:${h.asset.symbol}`;
  return `holding:${h.id}`;
}

function extractCurrentPricesFromHoldings(holdings: ParqetHolding[]): Record<string, number> {
  const result: Record<string, number> = {};
  for (const h of holdings) {
    const identifier = h.asset?.isin ?? h.asset?.symbol ?? h.id;
    if (!identifier) continue;
    const price =
      h.quote?.price ??
      h.position?.currentPrice ??
      (h.position?.currentValue != null && h.position?.shares != null && h.position.shares > 0
        ? h.position.currentValue / h.position.shares
        : 0);
    if (price > 0) {
      result[identifier] = price;
    }
  }
  return result;
}

/** Returns logo URLs by asset identifier (ISIN/symbol). */
function extractLogosFromHoldings(holdings: ParqetHolding[]): Record<string, string> {
  const result: Record<string, string> = {};
  for (const h of holdings) {
    const identifier = h.asset?.isin ?? h.asset?.symbol ?? h.id;
    if (!identifier || !h.logo || typeof h.logo !== 'string') continue;
    result[identifier] = h.logo;
  }
  return result;
}

/** Returns identifiers of holdings marked as sold (isSold: true). Used to exclude from portfolio positions. */
function extractSoldAssetIdentifiers(holdings: ParqetHolding[]): string[] {
  const result: string[] = [];
  for (const h of holdings) {
    const isSold = h.position?.isSold ?? h.isSold;
    if (!isSold) continue;
    const identifier = h.asset?.isin ?? h.asset?.symbol ?? h.id;
    if (identifier) {
      result.push(identifier);
    }
  }
  return result;
}

function mergePerformanceResponses(responses: ParqetPerformanceResponse[]): ParqetPerformanceResponse {
  if (responses.length === 0) {
    return {
      performance: { valuation: { atIntervalStart: 0, atIntervalEnd: 0 } },
      holdings: [],
      interval: { start: '', end: '' },
    };
  }
  if (responses.length === 1) return responses[0];

  const perf = responses[0].performance;
  const valuation = {
    atIntervalStart: responses.reduce((s, r) => s + (r.performance?.valuation?.atIntervalStart ?? 0), 0),
    atIntervalEnd: responses.reduce((s, r) => s + (r.performance?.valuation?.atIntervalEnd ?? 0), 0),
  };
  const mergedHoldingsMap = new Map<string, ParqetHolding>();
  for (const r of responses) {
    for (const h of r.holdings ?? []) {
      const key = holdingKey(h);
      const existing = mergedHoldingsMap.get(key);
      if (!existing) {
        mergedHoldingsMap.set(key, { ...h });
      } else if (existing.performance && h.performance) {
        existing.performance = {
          ...existing.performance,
          valuation: {
            atIntervalStart:
              (existing.performance.valuation?.atIntervalStart ?? 0) + (h.performance.valuation?.atIntervalStart ?? 0),
            atIntervalEnd:
              (existing.performance.valuation?.atIntervalEnd ?? 0) + (h.performance.valuation?.atIntervalEnd ?? 0),
          },
          unrealizedGains:
            existing.performance.unrealizedGains && h.performance.unrealizedGains
              ? {
                  inInterval: {
                    gainGross:
                      (existing.performance.unrealizedGains.inInterval?.gainGross ?? 0) +
                      (h.performance.unrealizedGains.inInterval?.gainGross ?? 0),
                    gainNet: 0,
                    returnGross: 0,
                    returnNet: 0,
                  },
                }
              : existing.performance.unrealizedGains ?? h.performance.unrealizedGains,
        };
      }
    }
  }

  return {
    performance: { ...perf, valuation },
    holdings: Array.from(mergedHoldingsMap.values()),
    interval: responses[0].interval ?? { start: '', end: '' },
  };
}

function mapActivityTypeToTransaktion(
  type: ParqetActivity['type']
): 'buy' | 'sell' | 'dividend' | 'interest' | 'transfer_in' | 'transfer_out' | null {
  switch (type) {
    case 'buy':
      return 'buy';
    case 'sell':
      return 'sell';
    case 'transfer_in':
      return 'transfer_in';
    case 'transfer_out':
      return 'transfer_out';
    case 'dividend':
      return 'dividend';
    case 'interest':
      return 'interest';
    case 'fees_taxes':
      return null;
    default:
      return null;
  }
}

/**
 * Convert Parqet activities to TransaktionsDaten for the rest of the app.
 * Uses holdingsMap to resolve asset names. Sorted by date ascending for FIFO.
 */
export function activitiesToTransaktionsDaten(
  activitiesWithPortfolio: Array<{ activity: ParqetActivity; portfolioName: string }>,
  holdingsMap: Map<string, ParqetHolding>
): TransaktionsDaten[] {
  const rows: TransaktionsDaten[] = [];
  for (const { activity: act, portfolioName } of activitiesWithPortfolio) {
    const type = mapActivityTypeToTransaktion(act.type);
    if (type === null) continue;

    const key = getAssetKey(act.asset);
    const identifier = getActivityIdentifier(act.asset);
    const holding = holdingsMap.get(key);
    const name = holding?.nickname ?? holding?.asset?.name ?? identifier ?? 'Unbekannt';

    rows.push({
      identifier,
      date: act.datetime,
      type,
      name,
      holdname: name,
      portfolio: portfolioName,
      price: act.price ?? 0,
      quantity: act.shares ?? 0,
      totalValue: act.amountNet ?? act.amount ?? 0,
      currency: act.currency ?? 'EUR',
      fees: act.fee ?? 0,
      tax: act.tax ?? 0,
    });
  }
  rows.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  return rows;
}

export interface FetchParqetDataResult {
  transaktionsDaten: TransaktionsDaten[];
  currentPricesByAsset?: Record<string, number>;
  logosByAsset?: Record<string, string>;
  soldAssetIdentifiers?: string[];
  portfolioCount: number;
  successCount: number;
  warning?: string;
}

/**
 * Fetch all portfolios, activities and performance from Parqet, then convert to TransaktionsDaten[].
 */
export async function fetchParqetData(accessToken: string): Promise<FetchParqetDataResult> {
  const config = getParqetConfig();
  const baseUrl = config.apiBase;

  const portfoliosRes = await fetchPortfoliosList(baseUrl, accessToken);
  const items = portfoliosRes.items ?? [];
  if (items.length === 0) {
    return { transaktionsDaten: [], portfolioCount: 0, successCount: 0, warning: 'Keine Portfolios gefunden.' };
  }

  const [activitiesResults, perfResults] = await Promise.all([
    Promise.allSettled(items.map((p) => fetchAllActivitiesPages(baseUrl, accessToken, p.id))),
    Promise.allSettled(items.map((p) => fetchPerformanceForPortfolio(baseUrl, accessToken, p.id))),
  ]);

  const activitiesWithPortfolio: Array<{ activity: ParqetActivity; portfolioName: string }> = [];
  for (let i = 0; i < items.length; i++) {
    const r = activitiesResults[i];
    if (r.status === 'fulfilled') {
      const portfolioName = items[i]?.name ?? 'Parqet';
      for (const act of r.value) {
        activitiesWithPortfolio.push({ activity: act, portfolioName });
      }
    }
  }
  activitiesWithPortfolio.sort(
    (a, b) =>
      new Date(a.activity.datetime).getTime() - new Date(b.activity.datetime).getTime()
  );

  const successfulPerf = (perfResults as PromiseSettledResult<ParqetPerformanceResponse>[])
    .filter((r): r is PromiseFulfilledResult<ParqetPerformanceResponse> => r.status === 'fulfilled')
    .map((r) => r.value);
  const merged = mergePerformanceResponses(successfulPerf);
  const holdingsMap = new Map<string, ParqetHolding>();
  for (const h of merged.holdings ?? []) {
    holdingsMap.set(holdingKey(h), h);
  }

  const transaktionsDaten = activitiesToTransaktionsDaten(activitiesWithPortfolio, holdingsMap);
  const currentPricesByAsset = extractCurrentPricesFromHoldings(merged.holdings ?? []);
  const logosByAsset = extractLogosFromHoldings(merged.holdings ?? []);
  const soldAssetIdentifiers = extractSoldAssetIdentifiers(merged.holdings ?? []);

  const failedActivities = activitiesResults.filter((r) => r.status === 'rejected').length;
  const failedPerf = perfResults.filter((r) => r.status === 'rejected').length;
  const successCount = items.length - Math.max(failedActivities, failedPerf);
  let warning: string | undefined;
  if (failedActivities > 0 || failedPerf > 0) {
    warning = `Daten von ${successCount} von ${items.length} Portfolios geladen.`;
  }

  return {
    transaktionsDaten,
    currentPricesByAsset,
    logosByAsset,
    soldAssetIdentifiers,
    portfolioCount: items.length,
    successCount,
    warning,
  };
}
